import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getCampaign,
  updateCampaign,
  getCampaignStats,
  getCampaignCodes,
  generateCampaignCodes,
  deleteCampaignCode,
  type Campaign,
  type CampaignStats,
  type VoucherCode,
  type CampaignQuestion,
} from "@/services/api/campaigns";
import { QuestionnaireBuilder } from "@/components/admin/QuestionnaireBuilder";
import { ADMIN_ROUTES } from "@/routes/admin";
import { PUBLIC_ROUTES } from "@/routes/public";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Save,
  Settings,
  Percent,
  FileQuestion,
  KeyRound,
  BarChart3,
  Gift,
  Copy,
  Trash2,
  Plus,
  ExternalLink,
  Link2,
} from "lucide-react";

export default function CampaignEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const _locale = useRouteLocale();
  const { uuid } = useParams<{ uuid: string }>();
  usePageMeta({
    title: t("admin.campaigns.metaTitle"),
    description: t("admin.campaigns.description"),
    locale: _locale,
    path: ADMIN_ROUTES.campaignEditor(_locale, uuid ?? ""),
    noindex: true,
  });
  const [searchParams] = useSearchParams();
  const currentLocale = _locale || "ro";
  const initialTab = searchParams.get("tab") || "general";

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    type: "hidden" as "form" | "access" | "hidden",
    enabled: false,
    startAt: "",
    endAt: "",
    discountType: "percentage" as "percentage" | "fixed",
    standardDiscount: 0,
    premiumDiscount: 0,
    thankYouMessage: "",
    ctaUrl: "",
    ctaText: "",
    displayMode: "all" as "step" | "all",
  });

  // Stats state
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Codes state
  const [codes, setCodes] = useState<VoucherCode[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [newCodeCount, setNewCodeCount] = useState(10);
  const [newCodePrefix, setNewCodePrefix] = useState("");
  const [newCodeMaxUses, setNewCodeMaxUses] = useState<number | "">(1);

  // Questions state
  const [questions, setQuestions] = useState<CampaignQuestion[]>([]);

  useEffect(() => {
    if (uuid) {
      loadCampaign();
    }
  }, [uuid]);

  useEffect(() => {
    if (activeTab === "stats" && campaign) {
      loadStats();
    }
    if (activeTab === "codes" && campaign) {
      loadCodes();
    }
    if (activeTab === "questions" && campaign && campaign.type === "form") {
      loadQuestions();
    }
  }, [activeTab, campaign]);

  const loadQuestions = async () => {
    if (!uuid || !campaign) return;
    // Questions are loaded with the campaign, so we just set them from the campaign data
    setQuestions(campaign.questions || []);
  };

  const loadCampaign = async () => {
    if (!uuid) return;
    try {
      setLoading(true);
      const data = await getCampaign(uuid);
      setCampaign(data);

      // Parse discount config
      const discountConfig = data.discountConfig;
      let discountType: "percentage" | "fixed" = "percentage";
      let standardDiscount = 0;
      let premiumDiscount = 0;

      if (discountConfig?.plans) {
        if (discountConfig.plans.standard) {
          standardDiscount = discountConfig.plans.standard.discount;
        }
        if (discountConfig.plans.premium) {
          premiumDiscount = discountConfig.plans.premium.discount;
        }
        const unit =
          discountConfig.plans.standard?.unit ??
          discountConfig.plans.premium?.unit;
        if (unit) {
          discountType = unit === "ron" ? "fixed" : "percentage";
        } else if (discountConfig.type === "fixed") {
          discountType = "fixed";
        }
      } else if (discountConfig?.type === "fixed") {
        discountType = "fixed";
      }

      setFormData({
        name: data.name,
        type: data.type,
        enabled: data.enabled,
        startAt: data.startAt ? data.startAt.slice(0, 16) : "",
        endAt: data.endAt ? data.endAt.slice(0, 16) : "",
        discountType,
        standardDiscount,
        premiumDiscount,
        thankYouMessage: data.thankYouMessage || "",
        ctaUrl: data.ctaUrl || "",
        ctaText: data.ctaText || "",
        displayMode: data.displayMode,
      });

      // Set questions if available
      if (data.questions) {
        setQuestions(data.questions);
      }
    } catch (error) {
      console.error("Failed to load campaign:", error);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!uuid) return;
    try {
      setLoadingStats(true);
      const data = await getCampaignStats(uuid);
      setStats(data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadCodes = async () => {
    if (!uuid) return;
    try {
      setLoadingCodes(true);
      const data = await getCampaignCodes(uuid);
      setCodes(data.items);
    } catch (error) {
      console.error("Failed to load codes:", error);
    } finally {
      setLoadingCodes(false);
    }
  };

  const handleSave = async () => {
    if (!uuid) return;
    try {
      setSaving(true);

      // Build discount config
      const discountConfig = {
        type: formData.discountType,
        plans: {
          standard: {
            discount: formData.standardDiscount,
            unit: formData.discountType === "fixed" ? ("ron" as const) : ("percent" as const),
          },
          premium: {
            discount: formData.premiumDiscount,
            unit: formData.discountType === "fixed" ? ("ron" as const) : ("percent" as const),
          },
        },
      };

      await updateCampaign(uuid, {
        name: formData.name,
        type: formData.type,
        enabled: formData.enabled,
        startAt: formData.startAt || null,
        endAt: formData.endAt || null,
        discountConfig,
        thankYouMessage: formData.thankYouMessage || null,
        ctaUrl: formData.ctaUrl || null,
        ctaText: formData.ctaText || null,
        displayMode: formData.displayMode,
      });

      toast.success(t("common.saved"));
      await loadCampaign();
    } catch (error: unknown) {
      console.error("Failed to save campaign:", error);
      const errorMessage = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(errorMessage || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCodes = async () => {
    if (!uuid) return;
    try {
      setGeneratingCodes(true);
      const maxUses = newCodeMaxUses === "" || newCodeMaxUses === 0 ? undefined : newCodeMaxUses;
      await generateCampaignCodes(uuid, {
        count: newCodeCount,
        prefix: newCodePrefix || undefined,
        singleUse: maxUses === 1,
        maxUses: maxUses && maxUses > 1 ? maxUses : undefined,
      });
      toast.success(t("admin.campaigns.editor.generatedSuccess", { count: newCodeCount }));
      await loadCodes();
    } catch (error) {
      console.error("Failed to generate codes:", error);
      toast.error(t("common.error"));
    } finally {
      setGeneratingCodes(false);
    }
  };

  const handleDeleteCode = async (codeId: number) => {
    if (!uuid) return;
    try {
      await deleteCampaignCode(uuid, codeId);
      toast.success(t("admin.campaigns.editor.codeDeleted"));
      await loadCodes();
    } catch (error: unknown) {
      console.error("Failed to delete code:", error);
      const errorMessage = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(errorMessage || t("common.error"));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t("common.copied"));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader currentLocale={currentLocale} active="campaigns" />
        <div className="container mx-auto px-4 py-12 min-h-[calc(100vh-4rem)]">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader currentLocale={currentLocale} active="campaigns" />
        <div className="container mx-auto px-4 py-12 min-h-[calc(100vh-4rem)]">
          <p className="text-center text-muted-foreground">{t("admin.campaigns.editor.notFound")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader currentLocale={currentLocale} active="campaigns" />
      <div className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ADMIN_ROUTES.campaigns(currentLocale))}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common.back")}
            </Button>
            <h1 className="text-2xl font-bold">{campaign.name}</h1>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t("common.save")}
              </>
            )}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="general" className="gap-2">
              <Settings className="h-4 w-4" />
              {t("admin.campaigns.editor.tabGeneral")}
            </TabsTrigger>
            <TabsTrigger value="discount" className="gap-2">
              <Percent className="h-4 w-4" />
              {t("admin.campaigns.editor.tabDiscount")}
            </TabsTrigger>
            {campaign.type === "form" && (
              <TabsTrigger value="questions" className="gap-2">
                <FileQuestion className="h-4 w-4" />
                {t("admin.campaigns.editor.tabQuestions")}
              </TabsTrigger>
            )}
            <TabsTrigger value="codes" className="gap-2">
              <KeyRound className="h-4 w-4" />
              {t("admin.campaigns.editor.tabCodes")}
            </TabsTrigger>
            <TabsTrigger value="completion" className="gap-2">
              <Gift className="h-4 w-4" />
              {t("admin.campaigns.editor.tabCompletion")}
            </TabsTrigger>
            <TabsTrigger value="stats" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              {t("admin.campaigns.editor.tabStats")}
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.campaigns.editor.generalTitle")}</CardTitle>
                <CardDescription>{t("admin.campaigns.editor.generalDescription")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("admin.campaigns.name")}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.campaigns.type")}</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(v) => setFormData({ ...formData, type: v as "form" | "access" | "hidden" })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="form">{t("admin.campaigns.typeForm")}</SelectItem>
                        <SelectItem value="access">{t("admin.campaigns.typeAccess")}</SelectItem>
                        <SelectItem value="hidden">{t("admin.campaigns.typeHidden")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="startAt">{t("admin.campaigns.editor.startDate")}</Label>
                    <Input
                      id="startAt"
                      type="datetime-local"
                      value={formData.startAt}
                      onChange={(e) => setFormData({ ...formData, startAt: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endAt">{t("admin.campaigns.editor.endDate")}</Label>
                    <Input
                      id="endAt"
                      type="datetime-local"
                      value={formData.endAt}
                      onChange={(e) => setFormData({ ...formData, endAt: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base">{t("admin.campaigns.enable")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("admin.campaigns.editor.enableDescription")}
                    </p>
                  </div>
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                    disabled={!campaign.canBeEnabled && !formData.enabled}
                  />
                </div>

                {!campaign.canBeEnabled && !formData.enabled && (
                  <p className="text-sm text-amber-600">
                    {t("admin.campaigns.editor.setDatesToEnable")}
                  </p>
                )}

                {/* Share Link Section */}
                {(campaign.type === "form" || campaign.type === "access") && (
                  <div className={`p-4 border rounded-lg space-y-3 ${!formData.enabled ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                    <div className="flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      <Label className="text-base">{t("admin.campaigns.editor.campaignLink")}</Label>
                    </div>
                    {!formData.enabled && (
                      <p className="text-sm text-amber-600 font-medium">
                        {t("admin.campaigns.editor.notEnabledWarning")}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t("admin.campaigns.editor.shareLinkHint")}
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={`${window.location.origin}${PUBLIC_ROUTES.campaignQuestionnaire(currentLocale, uuid!)}`}
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(`${window.location.origin}${PUBLIC_ROUTES.campaignQuestionnaire(currentLocale, uuid!)}`)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(PUBLIC_ROUTES.campaignQuestionnaire(currentLocale, uuid!), '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Discount Tab */}
          <TabsContent value="discount">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.campaigns.editor.discountTitle")}</CardTitle>
                <CardDescription>{t("admin.campaigns.editor.discountDescription")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>{t("admin.campaigns.editor.discountTypeLabel")}</Label>
                  <Select
                    value={formData.discountType}
                    onValueChange={(v) => setFormData({ ...formData, discountType: v as "percentage" | "fixed" })}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">{t("admin.campaigns.editor.discountTypePercentage")}</SelectItem>
                      <SelectItem value="fixed">{t("admin.campaigns.editor.discountTypeFixed")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <div className="p-4 border rounded-lg space-y-3">
                    <h4 className="font-medium">{t("admin.campaigns.editor.standardPlan")}</h4>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={formData.standardDiscount}
                        onChange={(e) => setFormData({ ...formData, standardDiscount: Number(e.target.value) })}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">
                        {formData.discountType === "percentage" ? "%" : "RON"}
                      </span>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg space-y-3">
                    <h4 className="font-medium">{t("admin.campaigns.editor.premiumPlan")}</h4>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={formData.premiumDiscount}
                        onChange={(e) => setFormData({ ...formData, premiumDiscount: Number(e.target.value) })}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">
                        {formData.discountType === "percentage" ? "%" : "RON"}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Questions Tab (only for form type) */}
          {campaign.type === "form" && (
            <TabsContent value="questions">
              <Card>
                <CardHeader>
                  <CardTitle>{t("admin.campaigns.editor.questionnaireTitle")}</CardTitle>
                  <CardDescription>{t("admin.campaigns.editor.questionnaireDescription")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>{t("admin.campaigns.editor.displayModeLabel")}</Label>
                      <Select
                        value={formData.displayMode}
                        onValueChange={(v) => setFormData({ ...formData, displayMode: v as "step" | "all" })}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("admin.campaigns.editor.displayModeAll")}</SelectItem>
                          <SelectItem value="step">{t("admin.campaigns.editor.displayModeStep")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        {formData.displayMode === "step"
                          ? t("admin.campaigns.editor.displayModeStepHint")
                          : t("admin.campaigns.editor.displayModeAllHint")}
                      </p>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-medium mb-4">{t("admin.campaigns.editor.tabQuestions")}</h4>
                      <QuestionnaireBuilder
                        campaignUuid={uuid!}
                        questions={questions}
                        onQuestionsChange={loadCampaign}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Codes Tab */}
          <TabsContent value="codes">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.campaigns.editor.codesTitle")}</CardTitle>
                <CardDescription>{t("admin.campaigns.editor.codesDescription")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Generate codes form */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium">{t("admin.campaigns.editor.generateCodesTitle")}</h4>
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="space-y-2">
                      <Label>{t("admin.campaigns.editor.countLabel")}</Label>
                      <Input
                        type="number"
                        min={1}
                        max={1000}
                        value={newCodeCount}
                        onChange={(e) => setNewCodeCount(Number(e.target.value))}
                        className="w-24"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("admin.campaigns.editor.prefixLabel")}</Label>
                      <Input
                        value={newCodePrefix}
                        onChange={(e) => setNewCodePrefix(e.target.value.toUpperCase())}
                        placeholder={t("admin.campaigns.editor.prefixPlaceholder")}
                        className="w-32"
                        maxLength={10}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("admin.campaigns.editor.maxUsesLabel")}</Label>
                      <Input
                        type="number"
                        min={0}
                        value={newCodeMaxUses}
                        onChange={(e) => setNewCodeMaxUses(e.target.value === "" ? "" : Number(e.target.value))}
                        placeholder="∞"
                        className="w-24"
                      />
                      <p className="text-xs text-muted-foreground">{t("admin.campaigns.editor.maxUsesHint")}</p>
                    </div>
                    <Button onClick={handleGenerateCodes} disabled={generatingCodes}>
                      {generatingCodes ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      {t("admin.campaigns.editor.generateBtn")}
                    </Button>
                  </div>
                </div>

                {/* Codes list */}
                {loadingCodes ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : codes.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium">{t("admin.campaigns.editor.colCode")}</th>
                          <th className="px-4 py-2 text-left text-sm font-medium">{t("admin.campaigns.editor.colUses")}</th>
                          <th className="px-4 py-2 text-left text-sm font-medium">{t("admin.campaigns.editor.colStatus")}</th>
                          <th className="px-4 py-2 text-right text-sm font-medium">{t("admin.campaigns.editor.colActions")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {codes.map((code) => (
                          <tr key={code.id}>
                            <td className="px-4 py-2 font-mono text-sm">{code.value}</td>
                            <td className="px-4 py-2 text-sm">
                              {code.usedCount} / {code.maxUses ?? "∞"}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {code.isValid ? (
                                <span className="text-green-600">{t("admin.campaigns.editor.statusValid")}</span>
                              ) : (
                                <span className="text-gray-500">{t("admin.campaigns.editor.statusExhausted")}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(code.value)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                {code.usedCount === 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCode(code.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground py-8 text-center">
                    {t("admin.campaigns.editor.noCodes")}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Completion Tab */}
          <TabsContent value="completion">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.campaigns.editor.completionTitle")}</CardTitle>
                <CardDescription>{t("admin.campaigns.editor.completionDescription")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="thankYouMessage">{t("admin.campaigns.editor.thankYouMessageLabel")}</Label>
                  <Textarea
                    id="thankYouMessage"
                    value={formData.thankYouMessage}
                    onChange={(e) => setFormData({ ...formData, thankYouMessage: e.target.value })}
                    placeholder={t("admin.campaigns.editor.thankYouMessagePlaceholder")}
                    rows={4}
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-base">{t("admin.campaigns.editor.ctaButtonLabel")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("admin.campaigns.editor.ctaButtonHint")}
                    </p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="ctaUrl">{t("admin.campaigns.editor.ctaUrlLabel")}</Label>
                      <Input
                        id="ctaUrl"
                        value={formData.ctaUrl}
                        onChange={(e) => setFormData({ ...formData, ctaUrl: e.target.value })}
                        placeholder={`/${currentLocale}/pachete`}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("admin.campaigns.editor.ctaUrlHint", { locale: currentLocale })}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ctaText">{t("admin.campaigns.editor.ctaTextLabel")}</Label>
                      <Input
                        id="ctaText"
                        value={formData.ctaText}
                        onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                        placeholder={t("admin.campaigns.ctaPlaceholder") || "Vezi pachetele"}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.campaigns.editor.statsTitle")}</CardTitle>
                <CardDescription>{t("admin.campaigns.editor.statsDescription")}</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingStats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : stats ? (
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-3xl font-bold">{stats.totalCodes}</p>
                      <p className="text-sm text-muted-foreground">{t("admin.campaigns.editor.statTotalCodes")}</p>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-3xl font-bold">{stats.usedCodes}</p>
                      <p className="text-sm text-muted-foreground">{t("admin.campaigns.editor.statUsedCodes")}</p>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-3xl font-bold">{stats.totalRedemptions}</p>
                      <p className="text-sm text-muted-foreground">{t("admin.campaigns.editor.statTotalRedemptions")}</p>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-3xl font-bold">{stats.assignedVouchers}</p>
                      <p className="text-sm text-muted-foreground">{t("admin.campaigns.editor.statAssignedVouchers")}</p>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-3xl font-bold">{stats.usedVouchers}</p>
                      <p className="text-sm text-muted-foreground">{t("admin.campaigns.editor.statUsedVouchers")}</p>
                    </div>
                    <div className="p-4 border rounded-lg text-center">
                      <p className="text-3xl font-bold">{stats.conversionRate}%</p>
                      <p className="text-sm text-muted-foreground">{t("admin.campaigns.editor.statConversionRate")}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground py-8 text-center">
                    {t("admin.campaigns.editor.noStats")}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
